import { useState, useEffect } from 'react';
import { onboardingSlides } from '../config/onboarding';
import { ApiClient } from '../api/client';
import { VideoBackground } from './VideoBackground';
import { useTranslation } from 'react-i18next';

interface OnboardingScreenProps {
  onComplete: () => void;
}

export function OnboardingScreen({ onComplete }: OnboardingScreenProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const [isBalanceLoaded, setIsBalanceLoaded] = useState(false);
  const [showBalanceAnimation, setShowBalanceAnimation] = useState(false);
  const { t } = useTranslation();
  
  const currentSlide = onboardingSlides[currentSlideIndex];
  const isLastSlide = currentSlideIndex === onboardingSlides.length - 1;
  const isBalanceSlide = currentSlide.type === 'balance';

  // Fetch user balance when reaching the balance slide
  useEffect(() => {
    if (isBalanceSlide && userBalance === null) {
      const fetchUserBalance = async () => {
        try {
          const userInfo = await ApiClient.getUserInfo();
          setUserBalance(userInfo.balance);
          setIsBalanceLoaded(true);
        } catch (error) {
          console.error('Failed to fetch user balance:', error);
          // Set fallback values if API call fails
          setUserBalance(250);
          setIsBalanceLoaded(true);
        }
      };
      
      fetchUserBalance();
    }
  }, [currentSlideIndex, isBalanceSlide, userBalance]);

  // Trigger balance animation when balance is loaded
  useEffect(() => {
    if (isBalanceLoaded && isBalanceSlide) {
      setTimeout(() => {
        setShowBalanceAnimation(true);
      }, 500);
    } else {
      setShowBalanceAnimation(false);
    }
  }, [isBalanceLoaded, isBalanceSlide]);

  const handleNext = async () => {
    if (isLastSlide) {
      if (isCompleting) return;
      
      setIsCompleting(true);
      try {
        await ApiClient.completeOnboarding();
        onComplete();
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
        // Don't show error to user, just proceed with UI update
      } finally {
        setIsCompleting(false);
      }
    } else {
      setDirection(1);
      setCurrentSlideIndex(prev => prev + 1);
    }
  };

  const handleDotClick = (index: number) => {
    setDirection(index > currentSlideIndex ? 1 : -1);
    setCurrentSlideIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (Math.abs(dragOffset) > 100) { // Threshold for swipe
      if (dragOffset > 0 && currentSlideIndex > 0) {
        setDirection(-1);
        setCurrentSlideIndex(currentSlideIndex - 1);
      } else if (dragOffset < 0 && !isLastSlide) {
        setDirection(1);
        setCurrentSlideIndex(currentSlideIndex + 1);
      }
    }
    setDragOffset(0);
  };

  // Calculate how many C-Roll videos can be created with the balance
  const calculateVideos = (balance: number) => {
    // Assuming 50 sparkles per C-Roll video
    return Math.floor(balance / 50);
  };

  // Render balance content for the balance slide
  const renderBalanceContent = () => {
    if (!isBalanceSlide || userBalance === null) return null;
    
    const videoCount = calculateVideos(userBalance);
    
    return (
      <div className={`balance-container ${showBalanceAnimation ? 'animate-in' : ''}`}>
        <div className="balance-title">{t('onboarding.balanceSlideTitle')}</div>
        
        <div className="sparkles-display">
          <div className="sparkles-icon">✨</div>
          <div className="sparkles-value">{userBalance}</div>
        </div>
        
        <div className="free-gift">
          <div className="gift-icon">🎁</div>
          <div className="gift-text">{t('onboarding.balanceFreeGiftText')}</div>
        </div>
        
        <div className="balance-info">
          <div className="equals-text">{t('onboarding.balanceEqualsText')}</div>
          <div className="video-count">
            <span className="count">{videoCount}</span>
            <span className="video-text">{t('onboarding.balanceVideoCountText', { count: videoCount })}</span>
          </div>
          <div className="sparkles-per-video">
            <span className="sparkles-icon-small">✨</span>
            <span>{t('onboarding.balanceRateText')}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="onboarding-screen">
      {/* Background for all slides */}
      <VideoBackground 
        videoUrl={currentSlide.video || ''} 
        opacity={0.6} 
        blur={3} 
        gradientColors={currentSlide.gradientColors}
      />
      
      <div 
        className="onboarding-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isBalanceSlide ? (
          renderBalanceContent()
        ) : (
          <>
            <div 
              className="slide-icon-wrapper"
              style={{
                transform: `translateX(${dragOffset}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease'
              }}
            >
              <div className="slide-icon" key={currentSlideIndex}>{currentSlide.icon}</div>
              <div className="icon-glow" />
            </div>
            <div 
              className="slide-text" 
              key={`text-${currentSlideIndex}`}
              style={{
                transform: `translateX(${dragOffset}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease'
              }}
            >
              <h2>{t(currentSlide.titleKey)}</h2>
              <p>{t(currentSlide.descriptionKey)}</p>
            </div>
          </>
        )}
      </div>
      
      <div className="onboarding-navigation">
        <div className="dots">
          {onboardingSlides.map((_, index) => (
            <div 
              key={index}
              className={`dot ${index === currentSlideIndex ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
        <button 
          onClick={handleNext} 
          className="gradient-button"
          disabled={isCompleting}
        >
          {isLastSlide ? (
            isCompleting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('onboarding.gettingStartedButton')}</span>
              </div>
            ) : (
              t('onboarding.getStartedButton')
            )
          ) : (
            t('create.navigation.next')
          )}
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 0.4; }
          100% { transform: scale(1); opacity: 0.8; }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes floatUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes sparkle {
          0% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.2) rotate(5deg); }
          50% { transform: scale(1) rotate(0deg); }
          75% { transform: scale(1.1) rotate(-5deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        @keyframes countUp {
          0% { transform: scale(0.8); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        .onboarding-screen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #1a1d21 0%, #121416 100%);
          color: white;
          overflow: hidden;
          touch-action: pan-x pinch-zoom;
        }

        .onboarding-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 16px;
          min-height: 0;
          user-select: none;
          position: relative;
          z-index: 1;
        }

        .slide-icon-wrapper {
          position: relative;
          margin-bottom: 24px;
          will-change: transform;
        }

        .slide-icon {
          position: relative;
          font-size: 56px;
          z-index: 2;
          animation: ${direction > 0 ? 'slideInRight' : 'slideInLeft'} 0.4s ease-out;
        }

        .icon-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          background: radial-gradient(circle, rgba(0, 122, 255, 0.2) 0%, rgba(0, 122, 255, 0) 70%);
          border-radius: 50%;
          z-index: 1;
          animation: pulse 2s ease-in-out infinite;
        }

        .slide-text {
          display: flex;
          flex-direction: column;
          gap: 12px;
          text-align: center;
          will-change: transform;
        }

        h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          line-height: 1.2;
          background: linear-gradient(135deg, #ffffff 0%, #e1e1e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -0.5px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        p {
          font-size: 16px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          max-width: 280px;
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
        }

        .onboarding-navigation {
          padding: 24px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          animation: fadeIn 0.6s ease-out 0.4s backwards;
          position: relative;
          z-index: 1;
        }

        .dots {
          display: flex;
          justify-content: center;
          gap: 8px;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.15);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .dot.active {
          background-color: #007AFF;
          box-shadow: 0 0 12px rgba(0, 122, 255, 0.5);
        }

        .gradient-button {
          background: linear-gradient(135deg, #007AFF 0%, #0056b3 100%);
          color: white;
          border: none;
          padding: 16px 32px;
          border-radius: 14px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 140px;
          box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
        }

        .gradient-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 122, 255, 0.4);
        }

        .gradient-button:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
        }

        .gradient-button:disabled {
          opacity: 0.8;
          cursor: not-allowed;
        }

        /* Balance slide styles */
        .balance-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 30px;
          width: 100%;
          max-width: 320px;
          opacity: 0;
        }

        .balance-container.animate-in {
          animation: fadeIn 0.8s ease-out forwards;
        }

        .balance-title {
          font-size: 32px;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #e1e1e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
          animation: floatUp 0.8s ease-out 0.2s both;
        }

        .sparkles-display {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          animation: floatUp 0.8s ease-out 0.4s both;
        }

        .sparkles-icon {
          font-size: 48px;
          animation: sparkle 3s ease-in-out infinite;
        }

        .sparkles-value {
          font-size: 72px;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #e1e1e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: countUp 1s ease-out;
        }

        .free-gift {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(76, 175, 80, 0.15);
          border: 1px solid rgba(76, 175, 80, 0.3);
          border-radius: 12px;
          padding: 10px 16px;
          animation: floatUp 0.8s ease-out 0.5s both;
        }

        .gift-icon {
          font-size: 24px;
          animation: sparkle 3s ease-in-out infinite;
        }

        .gift-text {
          font-size: 16px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }

        .balance-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          background: rgba(0, 122, 255, 0.1);
          border: 1px solid rgba(0, 122, 255, 0.2);
          border-radius: 16px;
          padding: 20px;
          width: 100%;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          animation: floatUp 0.8s ease-out 0.6s both;
        }

        .equals-text {
          font-size: 18px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: -5px;
        }

        .video-count {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 18px;
        }

        .count {
          font-size: 32px;
          font-weight: 700;
          color: #007AFF;
        }

        .video-text {
          font-weight: 500;
        }

        .sparkles-per-video {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }

        .sparkles-icon-small {
          font-size: 16px;
        }
      `}</style>
    </div>
  );
} 